import { request } from '@mairistem/tools';
import _ from 'lodash';
import React from 'react';

import { getPayeurs, searchPayeurs } from '@/routes';
import type { ApiResponseType, PayeurType } from '@/types';
import { formatPayeurLabel } from '@/utils';

const MIN_SEARCH_LENGTH = 2;
const DEBOUNCE_DELAY = 300;
const INITIAL_PER_PAGE = 10;

type PayeurOption = {
  key: number;
  value: number;
  text: string;
};

const usePayeurSearch = () => {
  const [loading, setLoading] = React.useState(false);
  const [payeurs, setPayeurs] = React.useState<PayeurType[]>([]);

  // Premiers payeurs chargés d'emblée, réaffichés quand la recherche est trop courte.
  const initialPayeursRef = React.useRef<PayeurType[]>([]);
  const abortControllerRef = React.useRef<AbortController | null>(null);

  React.useEffect(() => {
    let cancelled = false;

    request
      .get(getPayeurs({ page: 1, perPage: INITIAL_PER_PAGE }))
      .then(({ data }: ApiResponseType<PayeurType>) => {
        if (cancelled) return;
        initialPayeursRef.current = data;
        setPayeurs((prev) => (_.isEmpty(prev) ? data : prev));
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, []);

  const fetchPayeurs = React.useCallback((query: string) => {
    if (query.length < MIN_SEARCH_LENGTH) {
      setPayeurs(initialPayeursRef.current);
      setLoading(false);
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);

    request
      .get(searchPayeurs({ query, perPage: 10 }), { signal: controller.signal })
      .then(({ data }: ApiResponseType<PayeurType>) => {
        setPayeurs(data);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          setPayeurs([]);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });
  }, []);

  const debouncedFetch = React.useRef(
    _.debounce((query: string) => fetchPayeurs(query), DEBOUNCE_DELAY),
  );

  React.useEffect(
    () => () => {
      debouncedFetch.current.cancel();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    },
    [],
  );

  const onSearchChange = React.useCallback(
    (e: React.SyntheticEvent, { searchQuery }: { searchQuery: string }) => {
      debouncedFetch.current(searchQuery);
    },
    [],
  );

  const options = React.useMemo<PayeurOption[]>(
    () =>
      _.map(payeurs, (payeur) => ({
        key: payeur.identifiant,
        value: payeur.identifiant,
        text: formatPayeurLabel(payeur),
      })),
    [payeurs],
  );

  return { loading, options, payeurs, onSearchChange };
};

export default usePayeurSearch;
